// Export tickets to Excel by time period. Pick a preset (or a custom date
// range), optionally scoped to a single portal, and download an .xlsx straight
// from the agent's session (auth + org headers honoured via an axios blob).
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { DownloadSimple, CircleNotch } from '@phosphor-icons/react';
import { toast } from 'sonner';

const iso = (d) => d.toISOString().slice(0, 10);

// Build {from, to} (YYYY-MM-DD) for each preset; null = unbounded.
function presetRange(preset) {
  const today = new Date();
  const start = new Date(today);
  switch (preset) {
    case '7d': start.setDate(today.getDate() - 6); return { from: iso(start), to: iso(today) };
    case '30d': start.setDate(today.getDate() - 29); return { from: iso(start), to: iso(today) };
    case '90d': start.setDate(today.getDate() - 89); return { from: iso(start), to: iso(today) };
    case 'month': return { from: iso(new Date(today.getFullYear(), today.getMonth(), 1)), to: iso(today) };
    case 'year': return { from: iso(new Date(today.getFullYear(), 0, 1)), to: iso(today) };
    case 'all': return { from: '', to: '' };
    default: return { from: '', to: '' };
  }
}

const PRESETS = [
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: '90d', label: 'Last 90 days' },
  { key: 'month', label: 'This month' },
  { key: 'year', label: 'This year' },
  { key: 'all', label: 'All time' },
  { key: 'custom', label: 'Custom range' },
];

export default function ExportTicketsDialog({ open, onClose, portalId = null, portalName = null, filters = null }) {
  const { API, getOrgHeaders } = useAuth();
  const [preset, setPreset] = useState('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [busy, setBusy] = useState(false);

  const range = useMemo(() => (preset === 'custom' ? { from: customFrom, to: customTo } : presetRange(preset)), [preset, customFrom, customTo]);

  const doExport = async () => {
    if (preset === 'custom' && customFrom && customTo && customFrom > customTo) {
      toast.error('Start date must be before end date');
      return;
    }
    setBusy(true);
    try {
      const params = {};
      if (portalId) params.portal_id = portalId;
      if (range.from) params.from = range.from;
      if (range.to) params.to = range.to;
      // Carry the queue's active filters so the export matches the on-screen list.
      if (filters) {
        if (filters.status && filters.status !== 'all') params.status = filters.status;
        if (filters.priority && filters.priority !== 'all') params.priority = filters.priority;
        if (filters.category && filters.category !== 'all') params.category = filters.category;
        if (filters.q && filters.q.trim()) params.q = filters.q.trim();
        if (filters.assignee === 'me' && filters.userId) params.assignee_id = filters.userId;
        else if (filters.assignee === 'unassigned') params.unassigned = 'true';
        else if (filters.assignee && filters.assignee !== 'all') params.assignee_id = filters.assignee;
      }
      const res = await axios.get(`${API}/service-desk/tickets/export.xlsx`, {
        params, responseType: 'blob', withCredentials: true, headers: getOrgHeaders(),
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `service-desk-tickets-${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Tickets exported');
      onClose();
    } catch (e) {
      console.error('[export tickets]', e);
      toast.error('Export failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[460px]" data-testid="export-tickets-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DownloadSimple size={18} weight="fill" className="text-blue-600" />
            Export tickets{portalName ? ` · ${portalName}` : ''}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div>
            <Label className="text-xs text-zinc-500 mb-2 block">Time period</Label>
            <div className="grid grid-cols-2 gap-2" data-testid="export-presets">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPreset(p.key)}
                  className={`px-3 py-2 rounded-md text-sm font-medium ring-1 transition-colors text-left ${
                    preset === p.key ? 'bg-blue-600 text-white ring-blue-600' : 'bg-white text-zinc-600 ring-zinc-200 hover:bg-zinc-50'
                  }`}
                  data-testid={`export-preset-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {preset === 'custom' && (
            <div className="grid grid-cols-2 gap-3" data-testid="export-custom-range">
              <div>
                <Label className="text-xs text-zinc-500 mb-1 block">From</Label>
                <Input type="date" value={customFrom} max={customTo || undefined} onChange={(e) => setCustomFrom(e.target.value)} data-testid="export-from" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500 mb-1 block">To</Label>
                <Input type="date" value={customTo} min={customFrom || undefined} onChange={(e) => setCustomTo(e.target.value)} data-testid="export-to" />
              </div>
            </div>
          )}

          <p className="text-xs text-zinc-400">
            {range.from || range.to
              ? `Exports tickets created ${range.from || '…'} to ${range.to || 'today'}${portalName ? ` in ${portalName}` : ' across your portals'}.`
              : `Exports all tickets${portalName ? ` in ${portalName}` : ' across your portals'}.`}
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="export-cancel">Cancel</Button>
          <Button onClick={doExport} disabled={busy} data-testid="export-confirm">
            {busy ? <CircleNotch size={16} className="mr-1.5 animate-spin" /> : <DownloadSimple size={16} className="mr-1.5" />}
            {busy ? 'Exporting…' : 'Download Excel'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
